import React, { useEffect, useRef } from 'react';
import { Form, Input, Select } from 'formik-antd';
import { Formik, FormikProps } from 'formik';
import { Button, Divider, Modal } from 'antd';
import * as Yup from 'yup';

import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import SubmitButton from 'components/formik/SubmitButton';
import { BaseModalFooter, BaseModal } from 'components/wrappers/BaseModal';
import {
  getAdminNotificationOptions,
  mapRecipients,
} from 'utils/notificationUtils';
import {
  getNotificationRecipients,
  sendNotification,
} from 'redux/reducers/notificationSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  isRecipientsLoadingSelector,
  recipientsSelector,
} from 'redux/selectors/notificationSelectors';
import { maxTextInputCharactersLength } from 'utils/validationUtils';
import { getFullName } from 'utils/personUtils';
import { AdminNotificationType } from './enums';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import style from './SendNotificationModal.module.less';

type Props = {
  onClose: () => void;
};

interface FormValues {
  type?: AdminNotificationType;
  recipients: string[];
  notificationTitle: string;
  notificationDescription: string;
  emailSubject: string;
  emailText: string;
}

const recipientsAllValue = '-1';
const maxEmailTextCharactersLength = 10000;

const isInAppNotificationRequired = (
  notificationType?: AdminNotificationType,
) =>
  notificationType === AdminNotificationType.InAppNotification ||
  notificationType === AdminNotificationType.Both;

const isEmailNotificationRequired = (
  notificationType?: AdminNotificationType,
) =>
  notificationType === AdminNotificationType.EmailNotification ||
  notificationType === AdminNotificationType.Both;

const validationSchema = Yup.object({
  type: Yup.number().required('Field is required'),
  recipients: Yup.array()
    .of(Yup.string().defined())
    .min(1, 'Field is required'),
  notificationTitle: Yup.string()
    .trim()
    .when('type', {
      is: isInAppNotificationRequired,
      then: Yup.string()
        .required('Field is required')
        .max(
          maxTextInputCharactersLength,
          `Notification title should be ${maxTextInputCharactersLength} characters maximum`,
        ),
    }),
  notificationDescription: Yup.string()
    .trim()
    .when('type', {
      is: isInAppNotificationRequired,
      then: Yup.string()
        .required('Field is required')
        .max(
          maxTextInputCharactersLength,
          `Notification text should be ${maxTextInputCharactersLength} characters maximum`,
        ),
    }),
  emailSubject: Yup.string()
    .trim()
    .when('type', {
      is: isEmailNotificationRequired,
      then: Yup.string()
        .required('Field is required')
        .max(
          maxTextInputCharactersLength,
          `Email subject should be ${maxTextInputCharactersLength} characters maximum`,
        ),
    }),
  emailText: Yup.string()
    .trim()
    .when('type', {
      is: isEmailNotificationRequired,
      then: Yup.string()
        .required('Field is required')
        .max(
          maxEmailTextCharactersLength,
          `Email text should be ${maxEmailTextCharactersLength} characters maximum`,
        ),
    }),
});

export default function SendNotificationModal(props: Props) {
  const { onClose } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const notificationRecipients = useAppSelector(recipientsSelector);
  const isNotificationRecipientsLoading = useAppSelector(
    isRecipientsLoadingSelector,
  );

  useModalCloseOnNavigate(onClose);

  const initialValues: FormValues = {
    type: undefined,
    recipients: [],
    notificationTitle: '',
    notificationDescription: '',
    emailSubject: '',
    emailText: '',
  };

  useEffect(() => {
    dispatch(getNotificationRecipients());
  }, [dispatch]);

  const handleModalCancel = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    const {
      recipients,
      notificationTitle,
      notificationDescription,
      emailSubject,
      emailText,
    } = values;

    const isAllSelected = recipients.includes(recipientsAllValue);

    const inAppNotification = isInAppNotificationRequired(values.type)
      ? {
          recipientsIds: mapRecipients({
            selectedUserIds: recipients,
            allPossibleRecipients: notificationRecipients,
            notificationType: AdminNotificationType.InAppNotification,
            isAllSelected,
          }),
          title: notificationTitle,
          body: notificationDescription,
        }
      : undefined;

    const emailNotification = isEmailNotificationRequired(values.type)
      ? {
          recipientsEmails: mapRecipients({
            selectedUserIds: recipients,
            allPossibleRecipients: notificationRecipients,
            notificationType: AdminNotificationType.EmailNotification,
            isAllSelected,
          }),
          title: emailSubject,
          body: emailText,
        }
      : undefined;

    const result = await dispatch(
      sendNotification({
        inAppNotification,
        emailNotification,
      }),
    );

    if (sendNotification.fulfilled.match(result)) {
      BaseAlert.success('Notification has been sent.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleRecipientsChange = (
    value: string[],
    formik: FormikProps<FormValues>,
  ) => {
    if (value.includes(recipientsAllValue)) {
      formik.setFieldValue('recipients', [recipientsAllValue], false);
    }

    if (value.length > 1 && value.indexOf(recipientsAllValue) === 0) {
      formik.setFieldValue('recipients', value.slice(1), false);
    }
  };

  return (
    <BaseModal
      title="Send notification"
      open
      closable={true}
      destroyOnClose
      maxWidth={468}
      onCancel={handleModalCancel}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className={style.text}>
                Please select the notification type and the recipient.
              </div>

              <FormItem name="type" label="Notification type *">
                <Select
                  name="type"
                  placeholder="Select notification type"
                  className="full-width"
                >
                  {getAdminNotificationOptions().map((option) => (
                    <Select.Option
                      key={`select-notification-type-${option.value}`}
                      value={option.value}
                    >
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>

              <FormItem name="recipients" label="Recipient *">
                <FormikSelectWithLoading
                  isLoading={isNotificationRecipientsLoading}
                  name="recipients"
                  mode="multiple"
                  optionFilterProp="children"
                  placeholder="Select notification recipients"
                  className="full-width"
                  onChange={(value: string[]) =>
                    handleRecipientsChange(value, formik)
                  }
                >
                  {!isNotificationRecipientsLoading && (
                    <>
                      <Select.Option
                        key={`select-notification-recipient-`}
                        value={recipientsAllValue}
                      >
                        All
                      </Select.Option>

                      {notificationRecipients.map((recipient) => (
                        <Select.Option
                          key={`select-notification-recipient-${recipient.userId}`}
                          value={recipient.userId || ''}
                        >
                          {getFullName(recipient.firstName, recipient.lastName)}
                        </Select.Option>
                      ))}
                    </>
                  )}
                </FormikSelectWithLoading>
              </FormItem>

              {isInAppNotificationRequired(formik.values.type) && (
                <>
                  <Divider className="modal-divider" />

                  <div className="modal-title">In-app notification</div>

                  <FormItem
                    label="Notification title *"
                    name="notificationTitle"
                  >
                    <Input
                      name="notificationTitle"
                      placeholder="Enter notification title"
                      autoComplete="off"
                    />
                  </FormItem>

                  <FormItem
                    label="Notification text *"
                    name="notificationDescription"
                  >
                    <Input.TextArea
                      name="notificationDescription"
                      placeholder="Enter notification description"
                      autoSize={{ minRows: 4, maxRows: 4 }}
                      autoComplete="off"
                    />
                  </FormItem>
                </>
              )}

              {isEmailNotificationRequired(formik.values.type) && (
                <>
                  <Divider className="modal-divider" />

                  <div className="modal-title">Email notification</div>

                  <FormItem label="Email subject *" name="emailSubject">
                    <Input
                      name="emailSubject"
                      placeholder="Enter email subject"
                      autoComplete="off"
                    />
                  </FormItem>

                  <FormItem label="Email text *" name="emailText">
                    <Input.TextArea
                      name="emailText"
                      placeholder="Enter email text"
                      autoSize={{ minRows: 4, maxRows: 4 }}
                      autoComplete="off"
                    />
                  </FormItem>
                </>
              )}
            </div>

            <BaseModalFooter>
              <div>
                <Button
                  data-id="closePartnerModalBtn"
                  onClick={handleModalCancel}
                >
                  Cancel
                </Button>

                <SubmitButton type="primary" data-id="submitPartnerBtn">
                  Send
                </SubmitButton>
              </div>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
